import React, {Dispatch, SetStateAction} from "react";
import {
    AllStates,
    asJsonformsErrorTranslate,
    asJsonformsTranslate,
    JSON_FORMS_CONFIG,
    StepForm,
    TranslateFunction
} from '../Forms';

import schema from './degrees.schema.json';

import {JSONSchema7} from "json-schema";
import {JsonForms} from "@jsonforms/react";
import uischema from "./degrees.uischema.json";
import {materialCells, materialRenderers} from "@jsonforms/material-renderers";
import Ajv, {ErrorObject} from "ajv";
import {convertGradePoints, Degrees, getProgramTitle} from "./Degrees";
import Stack from "@mui/material/Stack";
import {I18nMessage} from "../Representation";
import {Translator} from "@jsonforms/core";

import deCountries from '../i18n/countries/de.json';
import enCountries from '../i18n/countries/en.json';
import {getApplicableRequirementsDefinition} from "../mappings/MappingsPage";
import {getDebug} from "../Data";
import addFormats from "ajv-formats";
import {ArrayErrorMessagesContext} from "../Wizard";
import ErrorMessageArrayLayoutRenderer, {materialArrayLayoutTester} from "../ErrorMessageArrayLayoutRenderer";

export const EMPTY_DEGREES: Degrees =
    {
        further_degree_programs: [],
        qualifying_degree: {}
    };

const FORM_NS = "degrees_form.";

export function getFinalGradeEligibilityInfo(allStates: AllStates) {
    const warningMessages: I18nMessage[] = [];
    const infoMessages: I18nMessage[] = [];

    const requirements = getApplicableRequirementsDefinition(allStates);
    const {degrees: [degrees, ]} = allStates;
    if (degrees === undefined) {
        return {warningMessages: ["missing_data"], infoMessages: [], eligibilityPoints: 0};
    }

    const convertedGpa = convertGradePoints(degrees.qualifying_degree.gpa, degrees.qualifying_degree);

    if (isNaN(convertedGpa)) {
        return {warningMessages: ["gpa_not_converted"], infoMessages: [], eligibilityPoints: 0};
    }

    // The conversion above takes care that the value is cut off behind the first decimal.
    const conversionI18nKey = {
        key: "converted_gpa",
        substitution: {converted_gpa: Number(convertedGpa).toFixed(1)}
    };
    if (convertedGpa > 3.2) {
        warningMessages.push("gpa_insufficient");
        infoMessages.push(conversionI18nKey);
    } else {
        infoMessages.push(conversionI18nKey);
    }


    const eligibilityPoints = warningMessages.length > 0 ? 0 :
        convertedGpa < requirements.gpaBoundary1  ? 55 : convertedGpa < requirements.gpaBoundary2 ? 45 : 35;

    if (getDebug())
        infoMessages.push("Eligibility points at most " + eligibilityPoints);

    return {warningMessages: warningMessages, infoMessages: infoMessages, eligibilityPoints: eligibilityPoints};
}


function roundCreditsToHalf(degrees: Degrees) {
    if (degrees.qualifying_degree.total_credits)
        degrees.qualifying_degree.total_credits = Math.round(degrees.qualifying_degree.total_credits * 2) / 2;

    degrees.further_degree_programs.forEach(program => {
        if (program.total_credits)
            program.total_credits = Math.round(program.total_credits * 2) / 2;
    });

    //
    // return {
    //     ...degrees,
    //     qualifying_degree: {
    //         ...degrees.qualifying_degree,
    //         total_credits: (degrees.qualifying_degree.total_credits != undefined) ? (Math.round(degrees.qualifying_degree.total_credits * 2) / 2) : undefined
    //     },
    //     further_degree_programs: degrees.further_degree_programs.map(degree => ({
    //         ...degree,
    //         total_credits: (degree.total_credits !== undefined) ? Math.round(degree.total_credits * 2) / 2 : undefined
    //     }))
    // };
}

export class DegreesForm extends StepForm {

    constructor() {
        super(schema as JSONSchema7);
    }

    getFormNs(): string | undefined {
        return FORM_NS;
    }

    getTitleKey() {
        return "degrees_title";
    };

    getPage([formData, setFormData]: [any, Dispatch<SetStateAction<any>>],  allStates: AllStates, t: TranslateFunction, lang: string) {
        const degrees = (formData as unknown) as Degrees;
        if (degrees === undefined) {
            return <React.Fragment/>;
        }

        // const degrees = structuredClone((formData as unknown) as Degrees);
        // degrees.further_degree_programs = [...degrees.further_degree_programs.map((dp : DegreeProgram, i: number) => Object.assign({}, dp, {index:i}))];
        const numberOfFurtherDegreePrograms = formData.further_degree_programs.length;

        const translator: Translator = asJsonformsTranslate(t, FORM_NS);

        const ajv = new Ajv({
            allErrors: true,
            verbose: true,
            strict: false
        })
        addFormats(ajv);
        ajv.validate(schema, degrees);
        const extraErrors = this.validateExtra(degrees, t);
        const ajvErrors = ajv.errors ?? [];
        const allErrors = [...ajvErrors, ...extraErrors];
        const arrayPath = "/further_degree_programs";

        const fdErrors = allErrors.filter(error => error.instancePath.startsWith(arrayPath));
        const fdErrorMessages : string[] = [];
        fdErrors
            .filter(error => error.instancePath === arrayPath)
            .forEach(error => fdErrorMessages.push(error.message ?? t("there_are_errors")));
        fdErrors
            .filter(error => error.instancePath.length > arrayPath.length)
            .map(error => {
                const subPath = error.instancePath.substring(arrayPath.length + 1);
                return parseInt(subPath.substring(0, Math.max(subPath.indexOf("/"), subPath.length)));

            }).reduce((acc, curr) => acc.includes(curr) ? acc : [...acc, curr], [] as number[])
            .forEach(fdIndex => fdErrorMessages.push(t("errors_in_program", { program_index: fdIndex + 1, program: (degrees.further_degree_programs[fdIndex].subject ? ("(\"" + degrees.further_degree_programs[fdIndex].subject + "\")") : "")})));

        const renderers = [
            ...materialRenderers,
            //register custom renderers
            { tester: materialArrayLayoutTester, renderer: ErrorMessageArrayLayoutRenderer },
        ];


        return (<React.Fragment>
            <Stack spacing={2}>
                <ArrayErrorMessagesContext.Provider value={fdErrorMessages}>
                    <JsonForms
                        schema={schema}
                        uischema={uischema}
                        data={formData}
                        renderers={renderers}
                        cells={materialCells}
                        config={JSON_FORMS_CONFIG}
                        i18n={{
                            translate:
                                ((key: string, defaultMessage: string | undefined = undefined, _context: any = undefined): string | undefined => {
                                    if (!key.endsWith("description") &&
                                        !key.endsWith("label") &&
                                        (key.startsWith("further_degree_programs.country.") ||
                                            key.startsWith("qualifying_degree.country."))) {
                                        const countryKey = key.substring(key.length - 2);
                                        let translation;
                                        if (lang === "de")
                                            translation = (deCountries as { [k: string]: string })[countryKey];
                                        else if (lang === "en")
                                            translation = (enCountries as { [k: string]: string })[countryKey];
                                        if (translation)
                                            return translation;
                                    }
                                    return translator(key, defaultMessage);
                                }) as Translator,
                            locale: lang,
                            translateError: asJsonformsErrorTranslate(t)
                        }}
                        additionalErrors={this.validateExtra(formData, t)}
                        onChange={({data}) => {
                            const newFurtherDegrees = data.further_degree_programs;
                            if (newFurtherDegrees.length < numberOfFurtherDegreePrograms) {
                                for (let i = 0; i < numberOfFurtherDegreePrograms; i++) {
                                    // Only one program can be removed at a time, therefore
                                    // all indices < numberOfFurtherDegreePrograms - 2 are valid
                                    // if index numberOfFurtherDegreePrograms - 1 is reached, the last element was deleted
                                    // otherwise, the first index at which the stored index is larger that the expected index
                                    // represents the index of the deleted degree
                                    if (i === numberOfFurtherDegreePrograms - 1 || newFurtherDegrees[i].index > i) {
                                        // we identify that i is the index of the removed further degree program.
                                        // since further degrees start at index 1 (index 0 is the qualifying degree), the index of the deleted degree is i + 1
                                        const deletedIndex = i + 1;
                                        // now lets scan all subsequent data, remove information about modules assigned to
                                        // degree program deletedIndex and shift the index of all degree program references > deletedIndex by -1


                                        const [modules, setModules] = allStates.modules;
                                        setModules(
                                            modules
                                                .filter(moduleList => moduleList.degree_program_index !== deletedIndex)
                                                .map(moduleList => ({...moduleList, degree_program_index: (moduleList.degree_program_index > deletedIndex ? moduleList.degree_program_index - 1 : moduleList.degree_program_index)}) )
                                        );


                                        const [mappings, setMappings] = allStates.mappings
                                        setMappings(mappings
                                            .filter(mapping => mapping.applicant_module_degree_program_index !== deletedIndex)
                                            .map(mapping => ({...mapping, applicant_module_degree_program_index: mapping.applicant_module_degree_program_index > deletedIndex ? mapping.applicant_module_degree_program_index - 1 : mapping.applicant_module_degree_program_index}))
                                        )
                                        break;
                                    }
                                }
                            }
                            roundCreditsToHalf(data);
                            setFormData(data);
                        }
                        }

                    />
                </ArrayErrorMessagesContext.Provider>
            </Stack>
        </React.Fragment>);
    }

    validate(newAjv: () => Ajv, data: any, t: TranslateFunction, allStates: AllStates): ErrorObject[] {
        const errors = super.validate(newAjv, data, t, allStates);

        const degrees = ((data as unknown) as Degrees);
        if (degrees === undefined) {
            return errors;
        }

        errors.push(...this.validateExtra(degrees, t));

        return errors;
    }

    validateExtra(degrees: Degrees, t: TranslateFunction) {
        const errors = [];
        const seenDegreePrograms = new Map<string, { instancePath: string, schemaPath: string }[]>();
        seenDegreePrograms.set(getProgramTitle(degrees.qualifying_degree), [{
            instancePath: "/qualifying_degree/subject",
            schemaPath: "#/properties/qualifying_degree/properties/subject/duplicate"
        }]);
        degrees.further_degree_programs.forEach((degreeProgram, i) => {
            const programTitle = getProgramTitle(degreeProgram);
            const paths = seenDegreePrograms.get(programTitle) ?? [];
            paths.push({
                instancePath: "/further_degree_programs/" + i + "/subject",
                schemaPath: "#/properties/further_degree_programs/items/properties/subject/duplicate"
            })
            seenDegreePrograms.set(programTitle, paths);
        });

        const primaryKey = t("combination_of") + " " +
            t(FORM_NS + "qualifying_degree.university.label") + ", " +
            t(FORM_NS + "qualifying_degree.degree_level.label") + ", " +
            t(FORM_NS + "qualifying_degree.subject.label");

        Array.from(seenDegreePrograms.values()).forEach(paths => {
            if (paths && paths.length > 1) {
                paths.forEach(path => {
                    errors.push(
                        {
                            keyword: "notUnique",
                            instancePath: path.instancePath,
                            schemaPath: path.schemaPath,
                            params: {primaryKey: primaryKey},
                            message: "must be unique"
                        })

                });
            }
        });

        let lowerBound: number | undefined = undefined;
        let upperBound: number | undefined = undefined;
        if (degrees.qualifying_degree.country === "DE") {
            lowerBound = 0.7;
            upperBound = 4.0;
        } else if (degrees.qualifying_degree.min && degrees.qualifying_degree.max) {
            lowerBound = (degrees.qualifying_degree.min < degrees.qualifying_degree.max ?
                degrees.qualifying_degree.min : degrees.qualifying_degree.max);
            upperBound = (degrees.qualifying_degree.min > degrees.qualifying_degree.max ?
                degrees.qualifying_degree.min : degrees.qualifying_degree.max);
        }

        if (lowerBound !== undefined && upperBound !== undefined && degrees.qualifying_degree.gpa !== undefined) {
            if (degrees.qualifying_degree.gpa < lowerBound) {
                errors.push({
                    instancePath: "/qualifying_degree/gpa",
                    schemaPath: "#/properties/qualifying_degree/properties/gpa/lower_bound",
                    keyword: "minimum",
                    params: {
                        comparison: ">=",
                        limit: lowerBound
                    },
                    message: "must be >= " + lowerBound,
                    schema: lowerBound
                });
            }
            if (degrees.qualifying_degree.gpa > upperBound) {
                errors.push({
                    instancePath: "/qualifying_degree/gpa",
                    schemaPath: "#/properties/qualifying_degree/properties/gpa/upper_bound",
                    keyword: "maximum",
                    params: {
                        comparison: "<=",
                        limit: upperBound
                    },
                    message: "must be <= " + upperBound,
                    schema: upperBound
                });
            }
        }

        if (!degrees.qualifying_degree.almost_complete) {
            errors.push({
                instancePath: "/qualifying_degree/almost_complete",
                schemaPath: "#/properties/qualifying_degree/properties/almost_complete/must_select",
                params: {},
                keyword: "must_select",
                message: "must be selected"
            });
        }

        if (degrees.qualifying_degree.total_credits !== undefined && (degrees.qualifying_degree.total_credits !== Math.round(degrees.qualifying_degree.total_credits * 2) / 2)) {
            const keyword = "invalid";
            const params = {
                actualValue: degrees.qualifying_degree.total_credits.toString(),
                nearestLegalValue: (Math.round(degrees.qualifying_degree.total_credits * 2) / 2).toString()
            };
            const defaultMsg = "only whole numbers and halt numbers allowed. {actualValue} {nearestLegalValue}";
            errors.push({
                keyword: keyword,
                instancePath: "/qualifying_degree/total_credits",
                schemaPath: "#/properties/qualifying_degree/properties/total_credits/incomplete",
                params: params,
                message: t("ajv_error." + keyword, defaultMsg, params)
            });
        }


        degrees.further_degree_programs.forEach((program, i) => {
            if (program.total_credits !== undefined && program.total_credits !== Math.round(program.total_credits * 2) / 2) {
                const keyword = "invalid";
                const params = {
                    actualValue: program.total_credits ? program.total_credits.toString() : "",
                    nearestLegalValue: program.total_credits ? (Math.round(program.total_credits * 2) / 2).toString() : ""
                };
                const defaultMsg = "only whole numbers and halt numbers allowed. {actualValue} {nearestLegalValue}";
                errors.push({
                    keyword: keyword,
                    instancePath: "/further_degree_programs/" + i + "/total_credits",
                    schemaPath: "#/properties/further_degree_programs/items/properties/total_credits/incomplete",
                    params: params,
                    message: t("ajv_error." + keyword, defaultMsg, params)
                });
            }
        })

        return errors;
    }

    getContextMessageKeys = (data: any, allStates: AllStates): [I18nMessage[], I18nMessage[]] => {
        const {warningMessages: warningMessages, infoMessages: infoMessages} = getFinalGradeEligibilityInfo(allStates);

        return [warningMessages, infoMessages];
    }

    filterValidationErrors(data: any, errors: ErrorObject[] | null | undefined) {
        if (errors === null || errors === undefined) {
            return errors;
        }

        const degrees = ((data as unknown) as Degrees);
        if (degrees === undefined) {
            return errors;
        }
        return errors.filter(error => {
            const path = error.instancePath;
            if (path.startsWith('/qualifying_degree') && (error.params.missingProperty === 'min' || error.params.missingProperty === 'max')) {
                return degrees.qualifying_degree.country !== "DE";
            }
            return true;
        });
    }

    getExplanationMarkdownKey(): string {
        return "degrees.explanation.markdown";
    }

    getHelpUrl(): string {
        return "faq.html#degrees";
    }

}